import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DisplayTechnology, UploadSoftwareUpdateRequest } from '../models/software-update.model';
import { md5 } from '../../../shared/utils/md5';
import { Modal } from '../../../shared/components/modal/modal';
import { ModalFooterDirective } from '../../../shared/components/modal/modal-footer.directive';
import { FormField } from '../../../shared/components/forms/form-field';
import { TextInput } from '../../../shared/components/forms/text-input';
import { Select } from '../../../shared/components/forms/select';

@Component({
  selector: 'app-upload-software-update-dialog',
  imports: [FormsModule, Modal, ModalFooterDirective, FormField, TextInput, Select],
  templateUrl: './upload-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UploadSoftwareUpdateDialogComponent {
  @Output() close = new EventEmitter<void>();
  @Output() save = new EventEmitter<UploadSoftwareUpdateRequest>();

  readonly open = signal(true);

  file: File | null = null;
  name = '';
  version = '';
  displayTechnology: DisplayTechnology = 'EPAPER';
  description = '';
  md5: string | null = null;
  isComputingMd5 = false;

  readonly technologies: DisplayTechnology[] = ['EPAPER', 'LCD', 'LED'];

  constructor(private cdr: ChangeDetectorRef) {}

  async onFileChange(event: Event) {
    const input = event.target as HTMLInputElement;
    this.file = input.files?.[0] ?? null;
    if (this.file && !this.name) {
      this.name = this.file.name.replace(/\.jar$/i, '');
    }
    input.value = '';

    this.md5 = null;
    if (this.file) {
      this.isComputingMd5 = true;
      this.cdr.markForCheck();
      this.md5 = await this.computeMd5(this.file);
      this.isComputingMd5 = false;
    }
    this.cdr.markForCheck();
  }

  private async computeMd5(file: File): Promise<string> {
    const buffer = await file.arrayBuffer();
    const bytes = new Uint8Array(buffer);
    return md5(bytes);
  }

  get isValid(): boolean {
    return !!this.file && !!this.name.trim() && !!this.version.trim() && !!this.displayTechnology && !this.isComputingMd5;
  }

  onSubmit() {
    if (!this.isValid || !this.file) return;
    this.save.emit({
      file: this.file,
      name: this.name.trim(),
      version: this.version.trim(),
      displayTechnology: this.displayTechnology,
      description: this.description.trim() || undefined,
      md5: this.md5 ?? undefined,
    });
  }

  onClose() {
    this.close.emit();
  }
}
